import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { Download, Play, RefreshCw, X, FileText } from 'lucide-react';
import { FileIndicator } from '../components/common/FileIndicator';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { FILE_SERVER_URL } from '../config/api';
import { toast } from 'react-toastify';

interface UpcomingSong {
  song_id: number;
  title: string;
  composer?: string;
  lyricist?: string;
  video_link?: string;
  placement: string;
  order_number: number;
  canonical_filename?: string;
  bulletin_file_path?: string;
  has_file?: boolean;
}

interface UpcomingService {
  service_id: number;
  liturgy_date: string;
  liturgy_name: string;
  time?: string;
  songs?: UpcomingSong[];
}

// Church options for the filter (IDs must match database)
const CHURCH_OPTIONS = [
  { id: 2, name: "St. Luke's Episcopal Church" },
  { id: 3, name: "San Lucas Iglesia Episcopal" }
];

const ChoirUpcomingServicesPage: React.FC = () => {
  const [services, setServices] = useState<UpcomingService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChurchId, setSelectedChurchId] = useState<number>(() => {
    const saved = localStorage.getItem('choirViewChurchId');
    return saved ? parseInt(saved, 10) : 2; // Default to St. Luke's (ID 2)
  });
  const [videoModal, setVideoModal] = useState<{ show: boolean; videoUrl: string; songTitle: string }>({
    show: false,
    videoUrl: '',
    songTitle: ''
  });

  useEffect(() => {
    fetchUpcomingServices();
  }, [selectedChurchId]);

  const handleChurchChange = (churchId: number) => {
    setSelectedChurchId(churchId);
    localStorage.setItem('choirViewChurchId', churchId.toString());
  };

  const fetchUpcomingServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/upcoming-services?weeksAhead=4&churchId=${selectedChurchId}`);
      const result = response.data;
      console.log('Services API response:', result);
      
      const servicesData = result.success && result.data && result.data.services ? result.data.services : [];
      
      // Debug video links
      servicesData.forEach((service: any) => {
        if (service.songs) {
          service.songs.forEach((song: any) => {
            if (song.video_link) {
              console.log(`Song "${song.title}" has video link: ${song.video_link}`);
            }
          });
        }
      });
      
      setServices(servicesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse date without timezone conversion - extract just the date part
    // This prevents UTC midnight from showing as previous day in local timezone
    const [datePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownload = async (song: UpcomingSong) => {
    if (!song.bulletin_file_path) {
      toast.warning(`No sheet music found for "${song.title}"`);
      return;
    }

    const filename = song.canonical_filename || song.bulletin_file_path.split('/').pop() || 'file';
    const url = `${FILE_SERVER_URL}/files/${song.bulletin_file_path}`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [downloadingServiceId, setDownloadingServiceId] = useState<number | null>(null);

  const handleDownloadAll = async (service: UpcomingService) => {
    if (!service.songs || service.songs.length === 0) {
      toast.warning('No songs in this service to download');
      return;
    }

    setDownloadingServiceId(service.service_id);
    try {
      const response = await apiClient.get(`/pdf-compilation/services/${service.service_id}/compile-pdf`, {
        responseType: 'blob'
      });

      // Check for missing songs header
      const missingSongsHeader = response.headers['x-songs-not-found'];
      if (missingSongsHeader) {
        const missingSongs = JSON.parse(missingSongsHeader);
        if (missingSongs.length > 0) {
          toast.info(`PDF generated. Missing files for: ${missingSongs.join(', ')}`);
        }
      }

      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${service.liturgy_name.replace(/[^a-zA-Z0-9]/g, '_')}_music.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      // Download the blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success('PDF downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading service PDF:', error);
      if (error.response?.status === 404) {
        toast.error('No files found for songs in this service');
      } else {
        toast.error('Failed to download PDF');
      }
    } finally {
      setDownloadingServiceId(null);
    }
  };

  const extractYouTubeVideoId = (url: string): string | null => {
    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/watch\?.*v=([^&\s]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handlePlayVideo = (link: string, songTitle: string) => {
    const videoId = extractYouTubeVideoId(link);
    if (videoId) {
      setVideoModal({
        show: true,
        videoUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        songTitle
      });
    } else {
      // Fallback: open in new tab if we can't extract the ID
      window.open(link, '_blank');
    }
  };

  const closeVideoModal = () => {
    setVideoModal({ show: false, videoUrl: '', songTitle: '' });
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <Container className="mt-4">
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" className="mb-3" />
              <p>Loading upcoming services...</p>
            </div>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <Container className="mt-4">
          <div className="d-flex align-items-center mb-4">
            <h1 className="mb-0">Choir View - Upcoming Services</h1>
          </div>
          <Alert variant="danger">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={fetchUpcomingServices}
            >
              <RefreshCw size={16} className="me-1" />
              Retry
            </button>
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container className="mt-4">
        <div className="d-flex align-items-center mb-3">
          <h1 className="mb-0">Upcoming Services</h1>
          <button
            className="btn btn-outline-primary btn-sm ms-auto"
            onClick={fetchUpcomingServices}
            disabled={loading}
          >
            <RefreshCw size={16} className="me-1" />
            Refresh
          </button>
        </div>

        <div className="d-flex align-items-center gap-3 mb-4">
          <Form.Label className="mb-0 fw-medium">Church:</Form.Label>
          <div className="btn-group">
            {CHURCH_OPTIONS.map((church) => (
              <button
                key={church.id}
                type="button"
                className={`btn ${selectedChurchId === church.id ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleChurchChange(church.id)}
              >
                {church.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-muted">
            View upcoming services and songs with quick access to sheet music and practice videos.
          </p>
        </div>

        {services.length === 0 ? (
          <Alert variant="info">
            No upcoming services found. Check back later or contact your choir director.
          </Alert>
        ) : (
          services.map((service) => (
            <Card key={service.service_id} className="mb-4 shadow-sm">
              <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  {formatDate(service.liturgy_date)} - {service.liturgy_name}
                  {service.time && ` at ${service.time}`}
                </h5>
                {service.songs && service.songs.length > 0 && (
                  <button
                    className="btn btn-light btn-sm"
                    onClick={() => handleDownloadAll(service)}
                    disabled={downloadingServiceId === service.service_id}
                    title="Download all sheet music as PDF"
                  >
                    {downloadingServiceId === service.service_id ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <FileText size={16} className="me-1" />
                    )}
                    Download All
                  </button>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {!service.songs || service.songs.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    <p>No songs have been selected for this service yet.</p>
                  </div>
                ) : (
                  <Table className="mb-0" striped hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '25%' }}>Service Order</th>
                        <th style={{ width: '50%' }}>Song Title</th>
                        <th style={{ width: '25%', textAlign: 'center' }}>Resources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {service.songs
                        .sort((a, b) => (a.order_number || 0) - (b.order_number || 0))
                        .map((song) => (
                        <tr key={song.song_id}>
                          <td>
                            <Badge bg="secondary" className="px-2 py-1">
                              {song.placement || 'No placement'}
                            </Badge>
                          </td>
                          <td className="fw-bold"><FileIndicator hasFile={song.has_file} />{song.title}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleDownload(song)}
                                title="Download Sheet Music"
                              >
                                <Download size={16} />
                              </button>

                              {song.video_link ? (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handlePlayVideo(song.video_link!, song.title)}
                                  title="Play Practice Video"
                                >
                                  <Play size={16} />
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  disabled
                                  title="Practice Video Not Available"
                                >
                                  <Play size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          ))
        )}

        <div className="text-center mt-5 mb-4">
          <p className="text-muted">
            <small>
              Click the download icon to get sheet music. Click the play icon to watch practice videos.
            </small>
          </p>
        </div>
      </Container>

      {/* YouTube Video Modal */}
      <Modal
        show={videoModal.show}
        onHide={closeVideoModal}
        size="lg"
        centered
        className="video-modal"
      >
        <Modal.Header className="bg-dark text-white">
          <Modal.Title>{videoModal.songTitle}</Modal.Title>
          <button
            type="button"
            className="btn btn-link text-white p-0 ms-auto"
            onClick={closeVideoModal}
          >
            <X size={24} />
          </button>
        </Modal.Header>
        <Modal.Body className="p-0 bg-black">
          {videoModal.videoUrl && (
            <div className="ratio ratio-16x9">
              <iframe
                src={videoModal.videoUrl}
                title={videoModal.songTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ChoirUpcomingServicesPage;